// Product management API service
import apiClient from './api'
import {
  Product,
  ProductCreate,
  ProductUpdate,
  Category,
  CategoryCreate,
  CategoryUpdate,
  Variant,
  VariantCreate,
  VariantUpdate,
  PaginatedResponse,
  BulkImportResult,
  ProductStatus,
} from '../types/product'

// Product API
export const productService = {
  // List products with pagination and filters
  listProducts: async (params: {
    page?: number
    per_page?: number
    category_id?: string
    status?: ProductStatus
    search?: string
  } = {}) => {
    // Cap per_page at 100 to avoid backend validation errors
    const safeParams = {
      ...params,
      per_page: params.per_page ? Math.min(params.per_page, 100) : 50,
    }
    const response = await apiClient.get<PaginatedResponse<Product>>('/admin/products', { params: safeParams })
    return response.data
  },

  // Get single product
  getProduct: async (productId: string) => {
    const response = await apiClient.get<Product>(`/admin/products/${productId}`)
    return response.data
  },

  // Create product
  createProduct: async (data: ProductCreate) => {
    const response = await apiClient.post<Product>('/admin/products', data)
    return response.data
  },

  // Update product
  updateProduct: async (productId: string, data: ProductUpdate) => {
    const response = await apiClient.put<Product>(`/admin/products/${productId}`, data)
    return response.data
  },

  // Update product status
  updateProductStatus: async (productId: string, status: ProductStatus) => {
    const response = await apiClient.patch<Product>(`/admin/products/${productId}/status`, { status })
    return response.data
  },

  // Delete product
  deleteProduct: async (productId: string) => {
    await apiClient.delete(`/admin/products/${productId}`)
  },

  // Bulk import products
  bulkImport: async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post<BulkImportResult>('/admin/products/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // Bulk export products
  bulkExport: async (params: { category_id?: string; status?: ProductStatus } = {}) => {
    const response = await apiClient.get('/admin/products/bulk-export', {
      params,
      responseType: 'blob',
    })
    return response.data
  },

  // Bulk update products
  bulkUpdate: async (data: {
    product_ids: string[]
    operation: 'update_category' | 'update_pricing' | 'update_stock' | 'update_status'
    data: any
  }) => {
    const response = await apiClient.post<{
      total_products: number
      successful: number
      failed: number
      results: Array<{
        product_id: string
        success: boolean
        error?: string
      }>
    }>('/admin/products/bulk-update', data)
    return response.data
  },

  // Upload product image
  uploadImage: async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post<{ url: string; filename: string; size: number }>(
      '/admin/products/images/upload',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    return response.data
  },

  // Upload product image for specific product
  uploadProductImage: async (productId: string, file: File, isMain: boolean = false) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post<{
      id: string
      original: string
      sizes: {
        thumbnail: { jpeg: string; webp: string }
        small: { jpeg: string; webp: string }
        medium: { jpeg: string; webp: string }
        large: { jpeg: string; webp: string }
      }
      upload_date: string
    }>(
      `/admin/products/${productId}/upload-image?is_main=${isMain}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    return response.data
  },
}

// Variant API
export const variantService = {
  // List variants for a product
  listVariants: async (productId: string) => {
    const response = await apiClient.get<PaginatedResponse<Variant>>(
      `/admin/products/${productId}/variants`
    )
    return response.data
  },

  // Get single variant
  getVariant: async (variantId: string) => {
    const response = await apiClient.get<Variant>(`/admin/products/variants/${variantId}`)
    return response.data
  },

  // Create variant
  createVariant: async (productId: string, data: VariantCreate) => {
    const response = await apiClient.post<Variant>(`/admin/products/${productId}/variants`, data)
    return response.data
  },

  // Update variant
  updateVariant: async (variantId: string, data: VariantUpdate) => {
    const response = await apiClient.put<Variant>(`/admin/products/variants/${variantId}`, data)
    return response.data
  },

  // Delete variant
  deleteVariant: async (variantId: string) => {
    await apiClient.delete(`/admin/products/variants/${variantId}`)
  },
}

// Category API
export const categoryService = {
  // List categories with pagination
  listCategories: async (params: { page?: number; per_page?: number; parent_id?: string } = {}) => {
    const response = await apiClient.get<PaginatedResponse<Category>>('/admin/categories/', {
      params,
    })
    return response.data
  },

  // Get category hierarchy
  getCategoryHierarchy: async (rootCategoryId?: string) => {
    const response = await apiClient.get<{ tree: Category[]; total_root_categories: number }>('/admin/categories/tree/', {
      params: { root_category_id: rootCategoryId },
    })
    return response.data.tree
  },

  // Get single category
  getCategory: async (categoryId: string) => {
    const response = await apiClient.get<Category>(`/admin/categories/${categoryId}/`)
    return response.data
  },

  // Create category
  createCategory: async (data: CategoryCreate) => {
    const response = await apiClient.post<Category>('/admin/categories/', data)
    return response.data
  },

  // Update category
  updateCategory: async (categoryId: string, data: CategoryUpdate) => {
    const response = await apiClient.put<Category>(
      `/admin/categories/${categoryId}/`,
      data
    )
    return response.data
  },

  // Delete category
  deleteCategory: async (categoryId: string) => {
    await apiClient.delete(`/admin/categories/${categoryId}/`)
  },

  // Reorder categories
  reorderCategories: async (categories: Array<{ id: string; sort_order: number }>) => {
    await apiClient.post('/admin/categories/reorder', { categories })
  },

  // Assign products to category
  assignProducts: async (categoryId: string, productIds: string[]) => {
    await apiClient.post(`/admin/categories/${categoryId}/assign-products/`, {
      product_ids: productIds,
    })
  },
}
